import { useCallback, useEffect, useRef } from 'react';

import { isSignedOutElsewhere, markSignedIn, readSessionEpoch } from '../auth/sessionEpoch';

/** How often an untouched tab re-checks. Focus and visibility changes check immediately. */
const POLL_INTERVAL_MS = 2 * 60 * 1000;

export interface UseSessionSyncOptions {
  /** Whether this tab currently holds a session. No point watching when it does not. */
  readonly isAuthenticated: boolean;
  /** Ends this tab's session. Called when a sign-out elsewhere is detected. */
  readonly onSignedOutElsewhere: () => void;
}

/**
 * Signs this tab out when any other CoOrdio app signs out.
 *
 * WHY this is needed: every product is its own origin with its own `sessionStorage`, so a sign-out
 * in CoordioCore cannot reach this app's token. B2C revokes its own session cookie but does not
 * invalidate access tokens it has already issued, so without this the tab keeps working — and keeps
 * showing data — until the token expires, up to an hour later.
 *
 * HOW it decides. The tab notes when it started trusting its session, then compares that against
 * the shared sign-out timestamp in the cookie. A newer sign-out than this tab's session means the
 * tab is stale. Deliberately a cookie comparison and never a network call: a failed request cannot
 * be told apart from a revoked session, and treating a dropped connection as a sign-out would throw
 * someone out of a half-finished form.
 *
 * WHEN it checks. On mount, whenever the tab regains focus or becomes visible, and every two
 * minutes otherwise. Focus is what matters in practice — the moment someone touches an abandoned
 * tab it closes, which is the shared-workstation case this exists for.
 *
 * Mirrors `useSessionSync` in CoordioCore; see `sessionEpoch.ts` for why the copy exists.
 */
export function useSessionSync({ isAuthenticated, onSignedOutElsewhere }: UseSessionSyncOptions): void {
  /**
   * When this tab's session began.
   *
   * Recorded on the transition into an authenticated state rather than read from the token, because
   * MSAL's cached token may predate this tab — what matters is when THIS tab started trusting it.
   */
  const sessionStartedAtRef = useRef(0);
  const signedOutRef = useRef(false);
  const onSignedOutElsewhereRef = useRef(onSignedOutElsewhere);
  onSignedOutElsewhereRef.current = onSignedOutElsewhere;

  useEffect(() => {
    if (!isAuthenticated) {
      sessionStartedAtRef.current = 0;
      signedOutRef.current = false;
      return;
    }

    if (sessionStartedAtRef.current !== 0) return;

    const now = Date.now();
    sessionStartedAtRef.current = now;

    // If the shared state already records a sign-out newer than this moment, another tab signed out
    // while this one was loading; the check below handles it on the next tick.
    const { signedOutAt } = readSessionEpoch();
    if (signedOutAt <= now) {
      // Announce the sign-in so tabs parked on a login screen know a session exists again.
      markSignedIn();
    }
  }, [isAuthenticated]);

  const check = useCallback(() => {
    if (!isAuthenticated || signedOutRef.current) return;
    if (!isSignedOutElsewhere(sessionStartedAtRef.current)) return;

    // Latch so a slow sign-out cannot be triggered twice.
    signedOutRef.current = true;
    onSignedOutElsewhereRef.current();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    check();

    const onVisible = () => {
      if (document?.visibilityState === 'visible') check();
    };

    window?.addEventListener('focus', check);
    document?.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(check, POLL_INTERVAL_MS);

    return () => {
      window?.removeEventListener('focus', check);
      document?.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [isAuthenticated, check]);
}
